import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Sparkles,
  Loader2,
  TrendingUp,
  BellRing,
  BellOff,
  Trash2,
  ArrowRight,
  Compass,
  ChevronRight,
  ScanEye,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { DiscoveryReport, Watchlist } from "@/lib/discovery-types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const ALL_SOURCES = [
  { id: "reddit", label: "Reddit" },
  { id: "twitter", label: "X / Twitter" },
  { id: "youtube", label: "YouTube" },
  { id: "quora", label: "Quora" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "forum", label: "Forums" },
];

const TEMPLATES = [
  "I sell protein snacks",
  "I run a home interior business",
  "Affordable ethnic wear for women",
  "WhatsApp automation for small businesses",
  "Ayurvedic hair oil for hair fall",
];

const VELOCITY_COLOR: Record<string, string> = {
  rising: "bg-violet-50 text-violet-700 border-violet-200",
  emerging: "bg-blue-50 text-blue-700 border-blue-200",
  stable: "bg-slate-100 text-slate-700 border-slate-200",
  saturated: "bg-amber-50 text-amber-800 border-amber-200",
  declining: "bg-rose-50 text-rose-700 border-rose-200",
};

export const DiscoveryHome: React.FC = () => {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [queryType, setQueryType] = useState("keyword");
  const [region, setRegion] = useState("India");
  const [days, setDays] = useState("30");
  const [sources, setSources] = useState<string[]>(["reddit", "twitter", "youtube", "quora"]);
  const [competitor, setCompetitor] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [wlKeyword, setWlKeyword] = useState("");

  const reports = useQuery<DiscoveryReport[]>({
    queryKey: ["discovery-reports"],
    queryFn: async () => (await api.get("/discovery/reports")).data,
  });

  const watchlist = useQuery<Watchlist[]>({
    queryKey: ["discovery-watchlist"],
    queryFn: async () => (await api.get("/discovery/watchlist")).data,
  });

  // Poll pending query until report ready
  useEffect(() => {
    if (!pendingId) return;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/discovery/query/${pendingId}`);
        if (data.status === "ready" && data.report_id) {
          clearInterval(timer);
          setPendingId(null);
          toast.success("Discovery report ready");
          qc.invalidateQueries({ queryKey: ["discovery-reports"] });
          nav(`/discovery/report/${data.report_id}`);
        } else if (data.status === "failed") {
          clearInterval(timer);
          setPendingId(null);
          toast.error("Discovery failed: " + (data.error || "unknown error"));
        }
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [pendingId, qc, nav]);

  const startSearch = useMutation({
    mutationFn: async () => {
      const payload = {
        query_text: query.trim(),
        query_type: queryType,
        region,
        language: "en",
        selected_sources: sources,
        date_range_days: parseInt(days),
        competitor_terms: competitor.split(",").map((s) => s.trim()).filter(Boolean),
      };
      return (await api.post("/discovery/search", payload)).data;
    },
    onSuccess: (data) => {
      toast.info("Scanning public conversations…");
      setPendingId(data.query_id);
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Search failed"),
  });

  const addWatch = useMutation({
    mutationFn: async () => (await api.post("/discovery/watchlist", {
      keyword_or_topic: wlKeyword.trim(),
      alert_enabled: true,
    })).data,
    onSuccess: () => {
      toast.success("Added to watchlist");
      setWlKeyword("");
      qc.invalidateQueries({ queryKey: ["discovery-watchlist"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const deleteWatch = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/discovery/watchlist/${id}`)).data,
    onSuccess: () => {
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["discovery-watchlist"] });
    },
  });

  const canSubmit = query.trim().length >= 2 && sources.length > 0;

  return (
    <DashboardLayout
      title="Discovery"
      subtitle="Find what the market is already asking for — before you spend on content, ads, or inventory."
      seoPath="/discovery"
      seoDescription="AI-powered market Discovery — scan Reddit, X, YouTube and Quora for demand, pain points and buying intent."
      seoKeywords={["market discovery","demand analysis","pain point mining","buying intent","AI market research"]}
    >
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 sg-card p-6" data-testid="discovery-search-form">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg bg-[color:var(--sg-violet)]/10 flex items-center justify-center">
              <ScanEye className="h-4 w-4 text-[color:var(--sg-violet)]" />
            </div>
            <div className="sg-label">Discover demand</div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">What are you exploring?</Label>
              <Textarea
                data-testid="discovery-query-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. Ayurvedic hair oil for hair fall  ·  Home interior design in Bangalore  ·  WhatsApp automation for SMBs"
                rows={2}
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setQuery(t)}
                    data-testid={`template-${t.slice(0, 10)}`}
                    type="button"
                    className="text-xs px-2.5 py-1 rounded-full bg-[color:var(--sg-muted-bg)] hover:bg-slate-200 border border-[color:var(--sg-border)] text-[color:var(--sg-fg)] transition-colors"
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Type</Label>
                <Select value={queryType} onValueChange={setQueryType}>
                  <SelectTrigger data-testid="query-type" className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="keyword">Keyword</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="niche">Niche / Category</SelectItem>
                    <SelectItem value="audience">Audience</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Region</Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger data-testid="region-select" className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="India">India</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="UK">United Kingdom</SelectItem>
                    <SelectItem value="Global">Global</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Time range</Label>
                <Select value={days} onValueChange={setDays}>
                  <SelectTrigger data-testid="days-select" className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Sources</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ALL_SOURCES.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 p-2 rounded border border-[color:var(--sg-border)] cursor-pointer hover:bg-[color:var(--sg-muted-bg)] text-sm"
                  >
                    <Checkbox
                      data-testid={`source-${s.id}`}
                      checked={sources.includes(s.id)}
                      onCheckedChange={(v: boolean) => {
                        if (v) setSources([...sources, s.id]);
                        else setSources(sources.filter((x) => x !== s.id));
                      }}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Competitor terms (optional, comma-separated)</Label>
              <Input
                data-testid="competitor-input"
                value={competitor}
                onChange={(e) => setCompetitor(e.target.value)}
                placeholder="e.g. Mamaearth, WOW"
              />
            </div>
            <Button
              data-testid="run-discovery"
              onClick={() => startSearch.mutate()}
              disabled={!canSubmit || startSearch.isPending || !!pendingId}
              className="w-full h-11 bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white font-semibold"
            >
              {startSearch.isPending || pendingId ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning public conversations…</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Run discovery</>
              )}
            </Button>
            {pendingId && (
              <div className="text-xs text-[color:var(--sg-muted)] text-center">
                Claude Sonnet 4.5 is analyzing signals across {sources.length} source{sources.length === 1 ? "" : "s"}. This typically takes 20–40 seconds.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="sg-card p-6" data-testid="watchlist-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="sg-label">Watchlist</div>
                <div className="font-display text-base font-semibold mt-1">
                  {watchlist.data?.length ?? 0} tracked
                </div>
              </div>
              <BellRing className="h-4 w-4 text-[color:var(--sg-muted)]" />
            </div>
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="watchlist-input"
                value={wlKeyword}
                onChange={(e) => setWlKeyword(e.target.value)}
                placeholder="Add keyword / topic"
                className="text-sm"
              />
              <Button
                size="sm"
                onClick={() => addWatch.mutate()}
                disabled={!wlKeyword.trim() || addWatch.isPending}
                data-testid="add-watchlist"
                className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white shrink-0"
              >
                Add
              </Button>
            </div>
            {watchlist.data && watchlist.data.length > 0 ? (
              <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                {watchlist.data.map((w) => (
                  <li key={w.watchlist_id} className="flex items-center justify-between text-sm p-1.5 rounded hover:bg-[color:var(--sg-muted-bg)] group" data-testid={`wl-${w.watchlist_id}`}>
                    <span className="truncate flex-1">{w.keyword_or_topic}</span>
                    <button
                      onClick={() => deleteWatch.mutate(w.watchlist_id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[color:var(--sg-danger)]"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-[color:var(--sg-muted)]">
                Save keywords to auto-scan them periodically (weekly alerts coming soon).
              </div>
            )}
          </div>

          <Link to="/discovery/trends" className="sg-card p-6 flex items-center justify-between hover:border-[color:var(--sg-fg)] transition-colors" data-testid="link-trends">
            <div>
              <div className="sg-label">Trends</div>
              <div className="font-display text-base font-semibold mt-1">
                See what's rising
              </div>
            </div>
            <TrendingUp className="h-5 w-5 text-[color:var(--sg-violet)]" />
          </Link>
        </div>
      </div>

      <div className="sg-card p-6 mt-6" data-testid="recent-reports">
        <div className="sg-label mb-1">Recent reports</div>
        <div className="font-display text-lg font-semibold tracking-tight mb-4">
          Your discovery library
        </div>
        {reports.isLoading ? (
          <div className="sg-skeleton h-48" />
        ) : reports.data && reports.data.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.data.map((r) => (
              <Link
                key={r.report_id}
                to={`/discovery/report/${r.report_id}`}
                data-testid={`report-card-${r.report_id}`}
                className="sg-card p-5 flex flex-col hover:border-[color:var(--sg-fg)] transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`sg-chip ${VELOCITY_COLOR[r.trend_velocity] || "bg-slate-100"}`}>
                    {r.trend_velocity}
                  </span>
                  <span className="text-xs text-[color:var(--sg-muted)]">
                    {new Date(r.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="font-display text-base font-semibold tracking-tight text-[color:var(--sg-fg)] line-clamp-2 mb-2">
                  {r.query_text}
                </div>
                <p className="text-xs text-[color:var(--sg-muted)] line-clamp-3 flex-1">
                  {r.summary?.top_opportunity || "Report ready"}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex gap-2">
                    <div className="text-xs">
                      <span className="text-[color:var(--sg-muted)]">Idea</span>{" "}
                      <span className="font-semibold">{r.idea_validation_score}</span>
                    </div>
                    <div className="text-xs">
                      <span className="text-[color:var(--sg-muted)]">Conf</span>{" "}
                      <span className="font-semibold">{r.confidence_score}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[color:var(--sg-muted)]" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No discoveries yet"
            description="Run your first search above. Try one of the template ideas — a report takes ~30 seconds."
            icon={<Compass className="h-5 w-5 text-[color:var(--sg-muted)]" />}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default DiscoveryHome;
